import { MemoizePipe } from '../../memoize.pipe';
import { CoveragePipe } from '../../pipes/coverage.pipe';
import { WeatherConditionPipe } from '../../pipes/weather-condition.pipe';
import { RoundPipe } from '../../round.pipe';
import { IsNilPipe } from 'src/app/pipes/is-nil.pipe';

import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { RouterModule } from '@angular/router';

import { TranslateModule } from '@ngx-translate/core';

import { MapService } from 'ngx-mapbox-gl';

import {
  FaIconComponent,
  FaLayersComponent,
  FaLayersTextComponent,
} from '@fortawesome/angular-fontawesome';

import { ALARM_ORDER } from '../../../../app/dashboard/gma-alarm/constants';
import { isNil } from '../../../../utils/utils';
import { WW_ICONS } from '../../constants';
import { GeoJSONSource } from 'mapbox-gl';

export const MODULES = [
  CommonModule,
  MatCardModule,
  RouterModule,
  TranslateModule,
];
export const COMPONENTS = [
  FaIconComponent,
  FaLayersComponent,
  FaLayersTextComponent,
];
export const PIPES = [
  RoundPipe,
  WeatherConditionPipe,
  CoveragePipe,
  MemoizePipe,
  IsNilPipe,
];

/**
 * Component that works together with `ClusterHtmlComponent` and shows either 'cluster markers' or 'normal markers'.
 * @param {MapboxGeoJSONFeature} feature  The feature that belongs to the marker.
 * @param {string} selectedParameter  The selected parameter on the tabs.
 * @param {Function} getMarkerColorClassFn  Function that decides the color class of the marker.
 * @param {Function} getGmaAlarmMarkerColorFn  Function that decides the color of GmaAlarm marker.
 * @param {EventEmitter<MapboxGeoJSONFeature>} clickStation  Emits the feature when a marker is clicked tro be handled on the parent component.
 */
@Component({
  selector: 'app-cluster-point',
  templateUrl: './cluster-point.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [...MODULES, ...COMPONENTS, ...PIPES],
})
export class ClusterPointComponent implements OnInit {
  private injector = inject(Injector);
  private mapService = inject(MapService);
  private ref = inject(ChangeDetectorRef);

  feature = input<any>();
  selectedParameter = input<string>();

  getMarkerColorClassFn = input<Function>();
  getGmaAlarmMarkerColorFn = input<Function>();

  clickStation = output<any>();

  markerValue = signal<number>(undefined);
  markerProperties = signal<any>(undefined);

  clusterFeature: any;
  clusterFeatures: any[];

  clusterMarkerColorClass = computed(() => {
    const getMarkerColorClassFn = this.getMarkerColorClassFn();
    const selectedParameter = this.selectedParameter();
    const markerValue = this.markerValue();

    return untracked(() => {
      if (selectedParameter === 'ww') return null;
      else if (
        !isNil(getMarkerColorClassFn) &&
        ((selectedParameter !== 'webcam' && selectedParameter !== 'gmaAlarm') ||
          (selectedParameter === 'gmaAlarm' && isNil(markerValue)))
      ) {
        return [
          'mgl-map__marker',
          getMarkerColorClassFn(selectedParameter, markerValue),
        ];
      } else return null;
    });
  });

  get isClusterMarker() {
    return this.feature().properties?.cluster;
  }

  constructor() {
    this.listenToFeatureChanges();
  }

  ngOnInit(): void {
    this.getMarkerProperties();
  }

  onClickStation(e: MouseEvent) {
    e.preventDefault();
    if (this.isClusterMarker) this.clickStation.emit(this.clusterFeature);
    else this.clickStation.emit(this.feature());
  }

  private getMarkerProperties() {
    const feature = this.feature();
    if (this.isClusterMarker) {
      this.getSource<GeoJSONSource>(feature.source).getClusterLeaves(
        feature.id,
        Infinity,
        0,
        (err, features) => {
          if (err) console.error(err);
          else {
            this.clusterFeatures = features;
            this.getMarkerValue();
            const featureProperties = this.clusterFeature.properties;
            this.markerProperties.set(
              !isNil(featureProperties) ? { ...featureProperties } : undefined
            );
            this.ref.detectChanges();
          }
        }
      );
    } else {
      this.getMarkerValue();
      const featureProperties = feature?.properties;
      this.markerProperties.set(
        !isNil(featureProperties) ? { ...featureProperties } : undefined
      );
      this.ref.detectChanges();
    }
  }

  private getMarkerValue() {
    const selectedParameter = this.selectedParameter();
    if (this.isClusterMarker) {
      if (selectedParameter === 'ww') {
        this.clusterFeature = this.getSortedValueWWFeature();
        this.markerValue.set(
          this.clusterFeature.properties?.[selectedParameter]
        );
      } else if (selectedParameter === 'vv' || selectedParameter === 'hcl') {
        this.clusterFeature = this.getMinValueFeature();
        this.markerValue.set(
          this.clusterFeature.properties?.[selectedParameter]
        );
      } else if (selectedParameter === 'gmaAlarm') {
        this.clusterFeature = this.getMaxValueGmaAlarmFeature();
        this.markerValue.set(
          this.clusterFeature.properties?.[selectedParameter]
        );
      } else {
        this.clusterFeature = this.getMaxValueFeature();
        this.markerValue.set(
          this.clusterFeature.properties?.[selectedParameter]
        );
      }
    } else {
      this.markerValue.set(this.feature()?.properties?.[selectedParameter]);
    }
  }

  private getSortedValueWWFeature() {
    const selectedParameter = this.selectedParameter();
    return this.clusterFeatures.reduce((acc, currentFeature) => {
      const accValue = acc.properties?.[selectedParameter];
      const actualValue = currentFeature.properties?.[selectedParameter];
      if (isNil(accValue) || isNil(actualValue)) return currentFeature;

      const accPosition = WW_ICONS[accValue].position;
      const actualPosition = WW_ICONS[actualValue].position;

      if (actualPosition >= accPosition) return currentFeature;
      else return acc;
    }, {});
  }

  private getMaxValueFeature() {
    const selectedParameter = this.selectedParameter();
    return this.clusterFeatures.reduce((acc, currentFeature) => {
      const accValue = acc.properties?.[selectedParameter];
      const actualValue = currentFeature.properties?.[selectedParameter];
      if (isNil(accValue) || isNil(actualValue)) return currentFeature;
      else if (actualValue >= accValue) return currentFeature;
      else return acc;
    }, {});
  }

  private getMinValueFeature() {
    const selectedParameter = this.selectedParameter();
    return this.clusterFeatures.reduce((acc, currentFeature) => {
      const accValue = acc.properties?.[selectedParameter];
      const actualValue = currentFeature.properties?.[selectedParameter];
      if (isNil(accValue) || isNil(actualValue)) return currentFeature;
      else if (actualValue <= accValue) return currentFeature;
      else return acc;
    }, {});
  }

  private getMaxValueGmaAlarmFeature() {
    const selectedParameter = this.selectedParameter();
    return this.clusterFeatures.reduce((acc, currentFeature) => {
      const accValue = acc.properties?.[selectedParameter];
      const actualValue = currentFeature.properties?.[selectedParameter];
      if (isNil(accValue) || isNil(actualValue)) return currentFeature;
      else {
        const accPosition = ALARM_ORDER.indexOf(accValue);
        const actualPosition = ALARM_ORDER.indexOf(actualValue);
        if (actualPosition >= accPosition) return currentFeature;
        else return acc;
      }
    }, {});
  }

  private getSource<T extends mapboxgl.AnySourceImpl>(sourceId: string) {
    return this.mapService.getSource(sourceId) as T;
  }

  private listenToFeatureChanges() {
    effect(
      () => {
        this.feature();

        untracked(() => {
          this.getMarkerProperties();
        });
      },
      { injector: this.injector }
    );
  }
}
